import { OperationalEquipmentDto, WorkAreaType } from '@operations/app/api'
import { useTranslate } from '@tolgee/react'
import { TableDateCell, TomTable, TomTableEmptyCell, useTomTable } from '@tom-ui/ui'
import { useTablePreferencesConfig } from '@tom-ui/utils'
import { MRT_ColumnDef, MRT_Row, MRT_RowSelectionState } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import { TomTableToolbarAlertBanner } from 'modules/ui/src/theme/components/table/organisms/TomTableToolbarAlertBanner'
import { useMemo, useState } from 'react'
import { useOperationEquipmentTableActions } from '../../hooks/useOperationEquipmentTableActions'
import { OperationalEquipmentUIStore } from '../../stores/operational-equipment.ui-store'
import { operationalEquipmentFilterPresets } from '../../tableConfigs/operationalEquipmentFilterConfigs'
import {
  getCarrierAssignments,
  getYardAssignmentText,
} from '../../tableConfigs/operationalEquipmentTableHelper'
import { OperationalEquipmentCarrierAssignment } from '../atoms/OperationalEquipmentCarrierAssignment'
import { OperationalEquipmentMenuOptions } from '../atoms/OperationalEquipmentMenuOptions'
import { OperationalEquipmentYardAssignment } from '../atoms/OperationalEquipmentYardAssignment'

interface Props {
  operationalEquipmentUIStore: OperationalEquipmentUIStore
}

export const OperationalEquipmentTable = observer(({ operationalEquipmentUIStore }: Props) => {
  const { t } = useTranslate()
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({}) //ts type available

  const getTableActions = useOperationEquipmentTableActions()

  const columns = useMemo<MRT_ColumnDef<OperationalEquipmentDto>[]>(
    () => [
      {
        accessorKey: 'equipmentType',
        header: t('equipmentType', 'Equipment type'),
        ...operationalEquipmentFilterPresets.searchMandatory(),
      },
      {
        accessorKey: 'name',
        header: t('equipmentName', 'Equipment name'),
        ...operationalEquipmentFilterPresets.searchMandatory(),
      },
      {
        accessorKey: 'operationalStatus',
        header: t('status', 'Status'),
        Cell: cell => t(cell.row.original.operationalStatus.toLowerCase()),
        ...operationalEquipmentFilterPresets.statusSelect<OperationalEquipmentDto>(t),
      },
      {
        accessorKey: 'lastJobDateTime',
        header: t('dateCommaTime', 'Date, time'),
        Cell: cell => <TableDateCell date={cell.row.original.lastJobDateTime} timeStyle='medium' />,
        ...operationalEquipmentFilterPresets.dateOptional(),
      },
      {
        accessorKey: 'lastJobOperatorName',
        header: t('user', 'User'),
        Cell: TomTableEmptyCell,
        ...operationalEquipmentFilterPresets.searchOptional(),
      },
      {
        header: t('areaAssigned', 'Area assigned'),
        accessorFn: row =>
          getYardAssignmentText(
            row.assignments?.filter(x => x.workAreaType === WorkAreaType.Yard) ?? [],
          ),
        Cell: cell => (
          <OperationalEquipmentYardAssignment
            assignments={
              cell.row.original.assignments?.filter(x => x.workAreaType === WorkAreaType.Yard) ?? []
            }
          />
        ),
        ...operationalEquipmentFilterPresets.searchOptional(),
      },
      {
        header: t('vesselAssigned', 'Vessel assigned'),
        accessorFn: row =>
          getCarrierAssignments(
            row.assignments?.filter(x => x.workAreaType === WorkAreaType.Crane) ?? [],
          )
            .map(v => v.carrierName)
            .join('|'),
        Cell: cell => (
          <OperationalEquipmentCarrierAssignment
            assignments={
              cell.row.original.assignments?.filter(x => x.workAreaType === WorkAreaType.Crane) ??
              []
            }
          />
        ),
        ...operationalEquipmentFilterPresets.searchOptional(),
      },
      {
        header: t('trainAssigned', 'Train assigned'),
        accessorFn: row =>
          getCarrierAssignments(
            row.assignments?.filter(x => x.workAreaType === WorkAreaType.Train) ?? [],
          )
            .map(v => v.carrierName)
            .join('|'),
        Cell: cell => (
          <OperationalEquipmentCarrierAssignment
            assignments={
              cell.row.original.assignments?.filter(x => x.workAreaType === WorkAreaType.Train) ??
              []
            }
          />
        ),
        ...operationalEquipmentFilterPresets.searchOptional(),
      },
    ],
    [t, operationalEquipmentUIStore],
  )

  const canSelectRow = (row: MRT_Row<OperationalEquipmentDto>) => {
    const selectedRowIndexes = Object.keys(rowSelection)
    const selectedEquipmentType = selectedRowIndexes.length
      ? operationalEquipmentUIStore.items[Number(selectedRowIndexes[0])]?.equipmentType
      : null

    return !selectedEquipmentType || selectedEquipmentType === row.original.equipmentType
  }

  const { savedPreferences, onPreferencesChange } = useTablePreferencesConfig(
    'operations/operational-equipment',
  )

  const table = useTomTable<OperationalEquipmentDto>(
    {
      data: operationalEquipmentUIStore.items,
      columns,
      enableTopToolbar: true,
      enableRowSelection: canSelectRow,
      onRowSelectionChange: setRowSelection,
      enableSelectAll: false,
      enableExpanding: false,
      renderRowActions: ({ row }) => <OperationalEquipmentMenuOptions id={row.original.id} />,
      state: { rowSelection },

      renderToolbarAlertBannerContent: ({ table }) => {
        const selectedRows = table.getSelectedRowModel().rows
        const dynamicActions = getTableActions(selectedRows)

        if (!dynamicActions.length) {
          return null
        }

        return <TomTableToolbarAlertBanner table={table} tableActions={dynamicActions} />
      },

      muiTableContainerProps: {
        sx: {
          maxHeight: 'calc(100vh - 200px)',
          minHeight: 'calc(100vh - 200px)',
        },
      },
    },
    undefined,
    undefined,
    { columns, savedPreferences, onPreferencesChange },
  )

  return <TomTable table={table} />
})
